import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, type Setup } from "../api";
import Chart, { type ChartOverlay } from "../components/Chart";

function findSetup(setups: Setup[], qs: URLSearchParams): Setup | undefined {
  const sid = qs.get("setup_id");
  if (sid) {
    const hit = setups.find((s) => s.setup_id === sid);
    if (hit) return hit;
  }
  const sym = qs.get("symbol");
  const tf = qs.get("tf");
  const leg = qs.get("leg_start_date");
  const end = qs.get("tr_end_date");
  return setups.find(
    (s) =>
      s.symbol === sym &&
      s.timeframe === tf &&
      s.leg_start_date === leg &&
      s.tr_end_date === end,
  );
}

export default function SetupDetail() {
  const [qs] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const prefilledSetup = (location.state as { setup?: Setup } | null)?.setup;
  const symbol = qs.get("symbol") ?? "";
  const tf = qs.get("tf") ?? "";

  const setupsQ = useQuery({
    queryKey: ["setups", { all: true }],
    queryFn: () => api.setups({ origin: "manual,rule,refined", limit: 1000 }),
  });

  const setup = useMemo(() => {
    const fromList = setupsQ.data ? findSetup(setupsQ.data.setups, qs) : undefined;
    return fromList ?? prefilledSetup;
  }, [setupsQ.data, qs, prefilledSetup]);

  // Fetch a tight date range around the setup so Alpaca only returns the
  // bars we need to render it — works even for old intraday setups that fall
  // outside the default lookback. Padding is in calendar days per timeframe.
  const range = useMemo(() => {
    const legStart = setup?.leg_start_date as string | undefined;
    const trEnd = setup?.tr_end_date as string | undefined;
    if (!legStart || !trEnd) return undefined;
    // Calendar-day padding sized so the bars array has enough cushion on
    // each side to satisfy the chart's "fit + cushion" auto-fit even on a
    // wide desktop viewport (~280 bars). Excess is fine: Alpaca caps to
    // whatever exists; the chart slices to the visible window.
    const padDays: Record<string, number> = {
      "1min": 3,
      "5min": 10,
      "30min": 15,
      "1hour": 30,
      "4hour": 90,
      daily: 200,
      weekly: 365 * 3,
      monthly: 365 * 12,
    };
    const pad = padDays[tf] ?? 60;
    const shift = (iso: string, days: number) => {
      const d = new Date(iso.slice(0, 10) + "T00:00:00Z");
      d.setUTCDate(d.getUTCDate() + days);
      return d.toISOString().slice(0, 10);
    };
    return {
      start: shift(legStart, -pad),
      end: shift(trEnd, pad),
    };
  }, [setup, tf]);

  const barsQ = useQuery({
    queryKey: ["bars", symbol, tf, range?.start ?? null, range?.end ?? null],
    queryFn: () => api.bars(symbol, tf, range?.start, range?.end),
    enabled: !!symbol && !!tf && !!range,
  });

  const deleteMut = useMutation({
    mutationFn: (sid: string) =>
      api.deleteSetup({ symbol, tf, setup_id: sid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["setups"] });
      navigate("/");
    },
  });

  const overlay = useMemo<ChartOverlay | undefined>(() => {
    if (!setup || !barsQ.data) return undefined;
    const bars = barsQ.data;
    // Bars are fetched live from Alpaca, so the array's positional indices
    // no longer line up with the setup's stored *_idx values. Re-resolve each
    // anchor by date: find the first bar whose date prefix is >= target, and
    // walk back one if the previous bar matches exactly.
    const findIdx = (date: unknown): number => {
      if (typeof date !== "string" || !date) return -1;
      const target = date.slice(0, 10);
      for (let i = 0; i < bars.t.length; i++) {
        const d = bars.t[i].slice(0, 10);
        if (d === target) return i;
        if (d > target) return Math.max(0, i - 1);
      }
      return bars.t.length - 1;
    };
    const legStart = findIdx(setup.leg_start_date);
    const legEnd = findIdx(setup.leg_end_date);
    const trStart = findIdx(setup.tr_start_date);
    const trEnd = findIdx(setup.tr_end_date);
    if (legStart < 0 || legEnd < 0 || trStart < 0 || trEnd < 0) return undefined;

    // Breakout has no date column; project it relative to tr_end so it lands
    // on the right bar after re-resolving indices.
    let breakout: ChartOverlay["breakout"] | undefined;
    if (
      setup.breakout_idx !== null &&
      setup.breakout_idx !== undefined &&
      setup.tr_end_idx !== undefined
    ) {
      const offset = Number(setup.breakout_idx) - Number(setup.tr_end_idx);
      const bi = trEnd + offset;
      if (bi >= 0 && bi < bars.t.length) {
        breakout = {
          idx: bi,
          with_trend: setup.breakout_direction === "with_trend",
        };
      }
    }

    return {
      spike: [legStart, legEnd],
      range: [trStart, trEnd],
      tr_high: Number(setup.tr_high),
      tr_low: Number(setup.tr_low),
      target: Number(setup.measured_move_target),
      breakout,
    };
  }, [setup, barsQ.data]);

  if (!symbol || !tf) {
    return (
      <p className="muted">
        Missing symbol/tf. <Link to="/">Back to list</Link>
      </p>
    );
  }
  if (barsQ.isLoading) return <p className="muted">Loading…</p>;
  if (barsQ.error) return <p className="muted">Failed to load bars.</p>;
  // While the setups list is loading/refetching and we don't have a setup
  // yet (no nav-state prefill), show a spinner instead of flashing "not
  // found".
  if (!setup && (setupsQ.isLoading || setupsQ.isFetching)) {
    return <p className="muted">Loading…</p>;
  }
  if (!setup) {
    return (
      <p className="muted">
        Setup not found. <Link to="/">Back to list</Link>
      </p>
    );
  }
  if (!barsQ.data) return null;

  const dirColor = setup.direction === "bull" ? "var(--good)" : "var(--bad)";
  const isManual = String(setup.origin) === "manual";

  return (
    <div>
      <div className="detail-header">
        <a
          href="/"
          style={{ fontSize: 13, cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            // Mirror the browser back button so scroll position on the list
            // is restored. Fall back to a hard nav if there's no history
            // (e.g. opened the detail URL directly).
            if (window.history.length > 1) navigate(-1);
            else navigate("/", { replace: true });
          }}
        >
          ← list
        </a>
        <h2>
          {setup.symbol} <span className="muted">· {setup.timeframe}</span>
        </h2>
        <span
          className="dir-pill"
          style={{ color: dirColor, borderColor: dirColor }}
        >
          {String(setup.direction).toUpperCase()}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 18, fontWeight: 700 }}>
          {Number(setup.score).toFixed(1)}
        </span>
      </div>

      <Chart bars={barsQ.data} overlay={overlay} height={420} />

      <table className="detail-table">
        <tbody>
          <Row k="Spike" v={`${setup.leg_start_date} → ${setup.leg_end_date}`} />
          <Row k="Range" v={`${setup.tr_start_date} → ${setup.tr_end_date}`} />
          <Row
            k="Range height"
            v={`${(Number(setup.tr_height_leg_pct) * 100).toFixed(0)}% of leg`}
          />
          <Row k="TR high" v={Number(setup.tr_high).toFixed(2)} />
          <Row k="TR low" v={Number(setup.tr_low).toFixed(2)} />
          <Row k="Target" v={Number(setup.measured_move_target).toFixed(2)} />
          <Row k="Origin" v={String(setup.origin)} />
          <Row
            k="Breakout"
            v={(setup.breakout_direction as string | null) ?? "—"}
          />
          {typeof setup.score_ml === "number" && (
            <Row k="ML score" v={(setup.score_ml * 100).toFixed(1)} />
          )}
          {typeof setup.expected_bars_to_breakout === "number" && (
            <Row
              k="Expected bars to breakout"
              v={setup.expected_bars_to_breakout.toFixed(1)}
            />
          )}
          {typeof setup.p_breakout_within_3 === "number" && (
            <Row
              k="P(breakout ≤ 3 bars)"
              v={`${(setup.p_breakout_within_3 * 100).toFixed(0)}%`}
            />
          )}
          {typeof setup.p_breakout_within_10 === "number" && (
            <Row
              k="P(breakout ≤ 10 bars)"
              v={`${(setup.p_breakout_within_10 * 100).toFixed(0)}%`}
            />
          )}
        </tbody>
      </table>

      {Array.isArray(setup.hazard_curve) && setup.hazard_curve.length > 1 && (
        <div className="hazard-block">
          <div className="muted small">Breakout hazard curve</div>
          <HazardSparkline
            values={setup.hazard_curve as number[]}
            horizons={
              Array.isArray(setup.hazard_curve_horizons)
                ? (setup.hazard_curve_horizons as number[])
                : null
            }
          />
        </div>
      )}

      {isManual && setup.setup_id && (
        <div className="action-footer" style={{ marginTop: 32 }}>
          <button
            className="danger"
            onClick={() => {
              if (confirm("Delete this manual setup? This cannot be undone.")) {
                deleteMut.mutate(setup.setup_id as string);
              }
            }}
            disabled={deleteMut.isPending}
            style={{ flex: 1 }}
          >
            {deleteMut.isPending ? "Deleting…" : "Delete setup"}
          </button>
        </div>
      )}
      {deleteMut.error instanceof ApiError && (
        <p className="error" style={{ textAlign: "center" }}>
          Delete failed: {deleteMut.error.message}
        </p>
      )}
    </div>
  );
}

function HazardSparkline({
  values,
  horizons,
}: {
  values: number[];
  horizons: number[] | null;
}) {
  // ViewBox is fixed; the <svg> stretches to fill its container and the
  // .hazard-sparkline class pins the aspect ratio so the curve has room
  // to breathe on desktop without going skyscraper-tall on mobile.
  const w = 400;
  const h = 140;
  const padX = 28;
  const padY = 14;
  const n = values.length;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const xs = values.map((_, i) => padX + (i * innerW) / (n - 1));
  const ys = values.map(
    (v) => padY + (1 - Math.max(0, Math.min(1, v))) * innerH,
  );
  const path = xs
    .map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`)
    .join(" ");
  const area = `${path} L${xs[xs.length - 1].toFixed(1)},${(padY + innerH).toFixed(1)} L${xs[0].toFixed(1)},${(padY + innerH).toFixed(1)} Z`;
  const gridYs = [0, 0.25, 0.5, 0.75, 1];
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="hazard-sparkline"
      role="img"
      aria-label="hazard curve"
    >
      {gridYs.map((g) => {
        const y = padY + (1 - g) * innerH;
        return (
          <g key={g}>
            <line
              x1={padX}
              x2={w - padX}
              y1={y}
              y2={y}
              stroke="var(--chart-grid)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={padX - 4}
              y={y + 3}
              fontSize={9}
              textAnchor="end"
              fill="var(--muted)"
            >
              {(g * 100).toFixed(0)}%
            </text>
          </g>
        );
      })}
      <path d={area} fill="var(--accent)" fillOpacity={0.12} />
      <path
        d={path}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
      {(() => {
        const xMin = horizons ? horizons[0] : 0;
        const xMax = horizons ? horizons[horizons.length - 1] : n - 1;
        const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
          x: padX + f * innerW,
          v: xMin + f * (xMax - xMin),
        }));
        return ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={t.x}
              x2={t.x}
              y1={padY + innerH}
              y2={padY + innerH + 3}
              stroke="var(--muted)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={t.x}
              y={h - 2}
              fontSize={9}
              fill="var(--muted)"
              textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}
            >
              {t.v.toFixed(t.v >= 10 ? 0 : 1)}
            </text>
          </g>
        ));
      })()}
    </svg>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <tr>
      <th>{k}</th>
      <td>{v}</td>
    </tr>
  );
}
